import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useCallData } from "../../../contexts/CallDataContext";
import { formatDuration, formatTimeStamp, updateCallBookingResolved } from "../../../api/callData";
import { useSearchParams } from "react-router-dom";

import { useWeek } from "../../../contexts/WeekContext";
import { startOfDay, endOfDay, isWithinInterval } from "date-fns";
import { parseCustomTimestamp } from "../../../utils/dateUtils";
import { useDataAccess } from "../../../hooks/useDataAccess";

// Import new components
import OrdersHeader from "../../../components/orders/OrdersHeader";
import OrdersFilters from "../../../components/orders/OrdersFilters";
import OrdersTable from "../../../components/orders/OrdersTable";
import OrdersCards from "../../../components/orders/OrdersCards";
import OrderDetails from "../../../components/orders/OrderDetails";
import { getOrderStatus, getOrderStatusColor, getTotalAmount, getEquipmentList } from "../../../utils/orderUtils";

const Orders = () => {
    const { data: callData, loading, error, setData, updateCall, refreshData: refreshCallData } = useCallData();
    const { dateRange } = useWeek();
    const [searchParams, setSearchParams] = useSearchParams();
    const [orders, setOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [animationStates, setAnimationStates] = useState({
        header: false,
        filters: false,
        content: false
    });

    // Filter states
    const [activeFilters, setActiveFilters] = useState({
        bookingTime: null,
        orderStatus: null,
        multipleEquipment: null
    });

    // State for order status dropdown
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);

    // Toggle button state for confirmed only vs all logs
    const [viewMode, setViewMode] = useState('confirmedOnly');

    const tableBodyRef = useRef(null);
    const rowRef = useRef(null);
    const cardRef = useRef(null);

    // Track the currently active filter
    const [activeFilter, setActiveFilter] = useState(null);

    // Mobile filters state
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const { makeCustomerApiRequest } = useDataAccess();

    // Handle refresh functionality
    const handleRefresh = async () => {
        if (isRefreshing) return;
        
        setIsRefreshing(true);
        try {
            await refreshCallData();
        } catch (error) {
            console.error('Failed to refresh orders data:', error);
        } finally {
            // Add a small delay to give visual feedback of refreshing
            setTimeout(() => setIsRefreshing(false), 200);
        }
    };

    // console.log('Order structure', orders);

    // Initialize state from URL parameters on component mount
    useEffect(() => {
        const orderId = searchParams.get('id');
        if (orderId && callData) {
            const order = callData.find(c => c.cart_data?.summary?.cart_id === orderId);
            if (order) {
                setSelectedOrder(order);
            }
        }

        // Initialize filters from URL
        const urlFilters = {};
        const filterKeys = ['bookingTime', 'orderStatus', 'multipleEquipment'];
        
        filterKeys.forEach(key => {
            const value = searchParams.get(key);
            if (value !== null) {
                if (key === 'multipleEquipment') {
                    urlFilters[key] = value === 'true';
                } else {
                    urlFilters[key] = value;
                }
            }
        });

        // Initialize viewMode from URL
        const urlViewMode = searchParams.get('viewMode');
        if (urlViewMode === 'allLogs') {
            setViewMode('allLogs');
            // Don't force orderStatus filter when in allLogs mode
        } else {
            setViewMode('confirmedOnly');
            // If viewMode is confirmedOnly, ensure orderStatus filter is set to 'Booking Confirmed'
            if (!urlFilters.orderStatus) {
                urlFilters.orderStatus = 'Booking Confirmed';
            }
        }

        if (Object.keys(urlFilters).length > 0) {
            setActiveFilters(prev => ({ ...prev, ...urlFilters }));
            // Set active filter based on what's in URL
            const activeKey = Object.keys(urlFilters).find(key => urlFilters[key] !== null);
            if (activeKey) {
                setActiveFilter(activeKey);
            }
        }
    }, [callData, searchParams]);

    // Filter calls to only show those with orders (booking_info or cart_data exists) and apply filters
    useEffect(() => {
        if (callData) {
            let ordersData = callData.filter(call => {
                // Check for cart_data structure
                if (call.cart_data) {
                    return call.cart_data.full_cart?.items?.length > 0 ||
                           call.cart_data.full_cart?.customer_info ||
                           call.cart_data.full_cart?.delivery;
                }
                // Check for legacy booking_info structure
                return call.booking_info &&
                    (call.booking_info.equipment_requested?.length > 0 ||
                     call.booking_info.customer_info ||
                     call.booking_info.delivery_info);
            });

            // Apply viewMode filter first
            if (viewMode === 'confirmedOnly') {
                ordersData = ordersData.filter(order => {
                    const bookingData = order.cart_data || order.booking_info;
                    return getOrderStatus(bookingData, order.booking_resolved) === 'Confirmed' && 
                           !order.booking_resolved;
                });
                // Ensure orderStatus filter is set to 'Confirmed' when viewMode is confirmedOnly
                if (activeFilters.orderStatus !== 'Confirmed') {
                    setActiveFilters(prev => ({ ...prev, orderStatus: 'Confirmed' }));
                    setActiveFilter('orderStatus');
                }
            }

            // Apply filters (these will work in conjunction with viewMode)
            if (activeFilters.orderStatus) {
                const statusFilter = activeFilters.orderStatus.toLowerCase();
                if (statusFilter === 'completed') {
                    ordersData = ordersData.filter(order => order.booking_resolved === true);
                } else {
                    ordersData = ordersData.filter(order => {
                        const bookingData = order.cart_data || order.booking_info;
                        const orderStatus = getOrderStatus(bookingData, order.booking_resolved).toLowerCase();
                        return orderStatus === statusFilter && !order.booking_resolved;
                    });
                }
            }

            if (activeFilters.bookingTime) {
                const timeFilter = activeFilters.bookingTime;
                ordersData.sort((a, b) => {
                    const timeA = a.metadata?.timestamp || '';
                    const timeB = b.metadata?.timestamp || '';
                    return timeFilter === 'asc' ? timeA.localeCompare(timeB) : timeB.localeCompare(timeA);
                });
            }

            if (activeFilters.multipleEquipment !== null) {
                if (activeFilters.multipleEquipment === true) {
                    // Show only orders with multiple equipment (contains comma)
                    ordersData = ordersData.filter(order => {
                        const bookingData = order.cart_data || order.booking_info;
                        const equipmentList = getEquipmentList(bookingData);
                        return equipmentList.includes(', ');
                    });
                } else {
                    // Show only orders with single equipment (no comma)
                    ordersData = ordersData.filter(order => {
                        const bookingData = order.cart_data || order.booking_info;
                        const equipmentList = getEquipmentList(bookingData);
                        return !equipmentList.includes(',');
                    });
                }
            }

            setOrders(ordersData);
        }
    }, [callData, activeFilters, viewMode]);

    // Add this function to filter orders by date range
    const getFilteredOrders = () => {
        if (!callData || !dateRange?.start || !dateRange?.end) return [];
        
        // First get all orders with booking info or cart data
        const allOrders = callData.filter(call => {
            // Check for cart_data structure
            if (call.cart_data) {
                return call.cart_data.full_cart?.items?.length > 0;
            }
            // Check for legacy booking_info structure
            return call.booking_info && call.booking_info.equipment_requested?.length > 0;
        });
        
        // Then filter by date range
        const rangeStart = startOfDay(dateRange.start);
        const rangeEnd = endOfDay(dateRange.end);
        
        return allOrders.filter(call => {
            const timestampString = call?.metadata?.timestamp;
            const callDate = parseCustomTimestamp(timestampString);
            
            if (!callDate) return false;
            
            return isWithinInterval(callDate, { start: rangeStart, end: rangeEnd });
        });
    };

    // Use this function where you need filtered order counts
    const filteredOrders = getFilteredOrders();
    const totalFilteredOrders = filteredOrders.length;



    // Animation effects
    useEffect(() => {
        const timer1 = setTimeout(() => setAnimationStates(prev => ({ ...prev, header: true })), 100);
        const timer2 = setTimeout(() => setAnimationStates(prev => ({ ...prev, filters: true })), 300);
        const timer3 = setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, content: true }));
            // After the load animation is complete, set isInitialLoad to false
            setIsInitialLoad(false);
        }, 500);

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
        };
    }, []);

    // Listen for page reset events from navbar
    useEffect(() => {
        const handlePageReset = (event) => {
            // Check if this page needs to reset based on its current state
            // If we have a selected order, reset to show only the orders list
            if (selectedOrder !== null) {
                setSelectedOrder(null);
            }
        };

        window.addEventListener('pageReset', handlePageReset);

        return () => {
            window.removeEventListener('pageReset', handlePageReset);
        };
    }, [selectedOrder]);

    // Listen for browser back/forward navigation
    useEffect(() => {
        const handlePopState = () => {
            // Get current URL parameters
            const currentParams = new URLSearchParams(window.location.search);
            const orderId = currentParams.get('id');
            
            // Handle order details state
            if (selectedOrder && !orderId) {
                // Close order details when back button removes ID
                setSelectedOrder(null);
            } else if (!selectedOrder && orderId && callData) {
                // Open order details when forward button adds ID
                const order = callData.find(c => c.metadata?.stream_sid === orderId);
                if (order) {
                    setSelectedOrder(order);
                }
            }
            
            // Handle filter changes
            const currentFilters = {};
            const filterKeys = ['bookingTime', 'orderStatus', 'multipleEquipment'];
            
            filterKeys.forEach(key => {
                const value = currentParams.get(key);
                if (value !== null) {
                    if (key === 'multipleEquipment') {
                        currentFilters[key] = value === 'true';
                    } else {
                        currentFilters[key] = value;
                    }
                } else {
                    currentFilters[key] = null;
                }
            });
            
            // Update filters
            setActiveFilters(currentFilters);
            
            // Update active filter
            const activeKey = Object.keys(currentFilters).find(key => currentFilters[key] !== null);
            setActiveFilter(activeKey || null);

            // Handle viewMode changes
            const currentViewMode = currentParams.get('viewMode');
            if (currentViewMode === 'allLogs') {
                setViewMode('allLogs');
            } else {
                setViewMode('confirmedOnly');
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [selectedOrder, callData]);

    // Listen for global refresh events
    useEffect(() => {
        const handleGlobalRefresh = () => {
            handleRefresh();
        };

        window.addEventListener('globalRefresh', handleGlobalRefresh);
        return () => window.removeEventListener('globalRefresh', handleGlobalRefresh);
    }, [isRefreshing]);



    // Get available status types from orders
    const getAvailableStatusTypes = () => {
        if (!callData || callData.length === 0) return ['Confirmed', 'Incomplete', 'Completed'];

        // Get status types from all call data (not filtered orders) to show all available options
        const allOrdersData = callData.filter(call => {
            // Check for cart_data structure
            if (call.cart_data) {
                return call.cart_data.full_cart?.items?.length > 0 ||
                       call.cart_data.full_cart?.customer_info ||
                       call.cart_data.full_cart?.delivery;
            }
            // Check for legacy booking_info structure
            return call.booking_info &&
                (call.booking_info.equipment_requested?.length > 0 ||
                 call.booking_info.customer_info ||
                 call.booking_info.delivery_info);
        });

        const statusTypes = [...new Set(allOrdersData.map(order => {
            const bookingData = order.cart_data || order.booking_info;
            return getOrderStatus(bookingData, order.booking_resolved);
        }))];
        const allStatuses = Array.from(new Set([...statusTypes, 'Completed']));
        return allStatuses.length > 0 ? allStatuses : ['Confirmed', 'Incomplete', 'Completed'];
    };

    // Handle status selection
    const handleStatusSelect = (status) => {
        const newFilters = {
            bookingTime: null,
            orderStatus: status,
            multipleEquipment: null
        };
        
        setActiveFilters(newFilters);
        setActiveFilter('orderStatus');
        setShowStatusDropdown(false);
        
        // When manually selecting a status, switch to allLogs mode to allow full filter interaction
        // However, if selecting 'Confirmed', switch to confirmedOnly mode for consistency
        if (status === 'Confirmed') {
            setViewMode('confirmedOnly');
        } else {
            setViewMode('allLogs');
        }

        // Add to browser history
        const newParams = new URLSearchParams(searchParams);
        newParams.set('viewMode', status === 'Confirmed' ? 'confirmedOnly' : 'allLogs');
        Object.entries(newFilters).forEach(([key, value]) => {
            if (value !== null) {
                newParams.set(key, value.toString());
            } else {
                newParams.delete(key);
            }
        });
        window.history.pushState({}, '', `?${newParams.toString()}`);
    };

    // Handle viewMode toggle
    const handleViewModeToggle = (mode) => {
        setViewMode(mode);
        
        // Update filters based on viewMode
        let newFilters = { ...activeFilters };
        if (mode === 'confirmedOnly') {
            newFilters.orderStatus = 'Confirmed';
            setActiveFilter('orderStatus');
        } else {
            // For allLogs, reset orderStatus filter to show all orders
            newFilters.orderStatus = null;
            setActiveFilter(null);
        }
        
        setActiveFilters(newFilters);

        // Add to browser history
        const newParams = new URLSearchParams(searchParams);
        newParams.set('viewMode', mode);
        Object.entries(newFilters).forEach(([key, value]) => {
            if (value !== null) {
                newParams.set(key, value.toString());
            } else {
                newParams.delete(key);
            }
        });
        window.history.pushState({}, '', `?${newParams.toString()}`);
    };

    // Handle filter clicks
    const handleFilterClick = (filterName) => {
        let newFilters = { ...activeFilters };
        let newActiveFilter = activeFilter;

        if (filterName === 'bookingTime') {
            // If clicking the same filter, toggle its direction
            if (activeFilter === filterName) {
                newFilters[filterName] = activeFilters[filterName] === 'asc' ? 'desc' : 'asc';
            } else {
                // If clicking a different filter, deactivate current and activate new one
                newFilters = {
                    bookingTime: null,
                    orderStatus: null,
                    multipleEquipment: null,
                    [filterName]: 'asc'
                };
                newActiveFilter = filterName;
            }
            
            // When using any filter, switch to allLogs mode to allow full interaction
            if (viewMode === 'confirmedOnly') {
                setViewMode('allLogs');
                // Update URL to reflect the mode change
                const newParams = new URLSearchParams(searchParams);
                newParams.set('viewMode', 'allLogs');
                window.history.pushState({}, '', `?${newParams.toString()}`);
            }
        } else if (filterName === 'orderStatus') {
            // Toggle dropdown for order status
            // When clicking orderStatus filter, switch to allLogs mode to allow full interaction
            if (viewMode === 'confirmedOnly') {
                setViewMode('allLogs');
                // Update URL to reflect the mode change
                const newParams = new URLSearchParams(searchParams);
                newParams.set('viewMode', 'allLogs');
                window.history.pushState({}, '', `?${newParams.toString()}`);
            }
            setShowStatusDropdown(!showStatusDropdown);
            return; // Don't update history for dropdown toggle
        } else if (filterName === 'multipleEquipment') {
            // Special handling for multiple equipment filter
            if (activeFilter === 'multipleEquipment') {
                // If already filtering, cycle through: multiple -> single -> off
                if (activeFilters.multipleEquipment === true) {
                    newFilters.multipleEquipment = false;
                } else {
                    newFilters.multipleEquipment = null;
                    newActiveFilter = null;
                }
            } else {
                // If not filtering, turn on multiple equipment filter
                newFilters = {
                    bookingTime: null,
                    orderStatus: null,
                    multipleEquipment: true
                };
                newActiveFilter = 'multipleEquipment';
            }
            
            // When using any filter, switch to allLogs mode to allow full interaction
            if (viewMode === 'confirmedOnly') {
                setViewMode('allLogs');
                // Update URL to reflect the mode change
                const newParams = new URLSearchParams(searchParams);
                newParams.set('viewMode', 'allLogs');
                window.history.pushState({}, '', `?${newParams.toString()}`);
            }
        }

        // Update state
        setActiveFilters(newFilters);
        setActiveFilter(newActiveFilter);

        // Add to browser history
        const newParams = new URLSearchParams(searchParams);
        Object.entries(newFilters).forEach(([key, value]) => {
            if (value !== null) {
                newParams.set(key, value.toString());
            } else {
                newParams.delete(key);
            }
        });
        window.history.pushState({}, '', `?${newParams.toString()}`);
    };

    // Reset all filters
    const resetFilters = () => {
        setActiveFilters({
            bookingTime: null,
            orderStatus: null,
            multipleEquipment: null
        });
        setActiveFilter(null);
        setViewMode('allLogs');

        // Add to browser history
        const newParams = new URLSearchParams(searchParams);
        newParams.set('viewMode', 'allLogs');
        Object.keys(activeFilters).forEach(key => {
            newParams.delete(key);
        });
        window.history.pushState({}, '', `?${newParams.toString()}`);
    };

    // Check if any filter is active
    const isAnyFilterActive = activeFilter !== null;



    const handleMarkCompleted = async (callId, resolved) => {
        const originalCall = callData.find(c => c.metadata?.stream_sid === callId);
        if (!originalCall) return;

        const originalSelectedOrder = selectedOrder;

        // Optimistically update the UI
        const optimisticUpdate = { ...originalCall, booking_resolved: resolved };
        updateCall(callId, optimisticUpdate);
        if (selectedOrder?.id === callId) {
            setSelectedOrder(optimisticUpdate);
        }

        try {
            // Make the API request
            const updatedCallFromServer = await makeCustomerApiRequest(
                `calls/${callId}`,
                {
                    method: 'PATCH',
                    body: JSON.stringify({ booking_resolved: resolved }),
                }
            );

            // API call was successful, update state with the definitive data from the server
            updateCall(callId, updatedCallFromServer);
            if (selectedOrder?.id === callId) {
                setSelectedOrder(updatedCallFromServer);
            }
        } catch (error) {
            console.error("Failed to mark as completed:", error);
            // Revert to original state on error
            updateCall(callId, originalCall);
            if (selectedOrder?.id === callId) {
                setSelectedOrder(originalSelectedOrder);
            }
            // TODO: Show an error toast/notification to the user
        }
    };

    if (loading) {
        return (
            <div className="responsive-page-layout">
                <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                        <Icon icon="eos-icons:loading" className="text-4xl text-primary animate-spin mb-4" />
                        <p className="text-font-secondary">Loading orders...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="responsive-page-layout">
                <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                        <Icon icon="material-symbols:error-outline" className="text-4xl text-error mb-4" />
                        <p className="text-error">Error loading orders: {error?.message || error?.toString() || 'Unknown error occurred'}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="responsive-page-layout">
            {/* Header */}
            <OrdersHeader 
                animationStates={animationStates}
                viewMode={viewMode}
                handleViewModeToggle={handleViewModeToggle}
            />

            {/* Content */}
            <div className={`w-full flex-1 flex flex-col gap-3 min-h-0 transition-all duration-500 ease-out ${
                animationStates.content
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
            }`}>
                {/* Summary Data or Breadcrumbs */}
                {!selectedOrder ? (
                    <OrdersFilters 
                        animationStates={animationStates}
                        activeFilters={activeFilters}
                        activeFilter={activeFilter}
                        showStatusDropdown={showStatusDropdown}
                        setShowStatusDropdown={setShowStatusDropdown}
                        handleFilterClick={handleFilterClick}
                        handleStatusSelect={handleStatusSelect}
                        resetFilters={resetFilters}
                        isAnyFilterActive={isAnyFilterActive}
                        totalFilteredOrders={totalFilteredOrders}
                        showMobileFilters={showMobileFilters}
                        setShowMobileFilters={setShowMobileFilters}
                        getAvailableStatusTypes={getAvailableStatusTypes}
                    />
                ) : (
                    <div className="flex items-center gap-1 text-font-primary">
                        <button
                            onClick={() => {
                                // Add to browser history when closing order details
                                const newParams = new URLSearchParams(searchParams);
                                newParams.delete('id');
                                window.history.pushState({}, '', `?${newParams.toString()}`);
                                setSelectedOrder(null);
                            }}
                            className="flex items-center justify-center rounded-lg hover:bg-bgAccent transition-colors cursor-pointer"
                            title="Go back to orders"
                        >
                            <Icon icon="mdi:chevron-left" className="text-3xl text-accent hover:text-primary transition-colors" />
                        </button>
                        <span 
                            className="flex items-center justify-center gap-1 group transition-colors cursor-pointer"
                            onClick={() => {
                                // Add to browser history when closing order details
                                const newParams = new URLSearchParams(searchParams);
                                newParams.delete('id');
                                window.history.pushState({}, '', `?${newParams.toString()}`);
                                setSelectedOrder(null);
                            }}
                        >
                            <Icon icon="material-symbols:order-approve-outline" className="text-xl text-font-secondary group-hover:text-primary transition-colors" />
                            <span className="text-font-secondary group-hover:text-font-primary transition-colors">Orders</span>
                        </span>
                        <Icon icon="mdi:chevron-right" className="text-lg text-font-secondary" />
                        <span className="flex items-center justify-center gap-1">
                            <Icon icon="material-symbols:info-outline" className="text-2xl text-primary" />
                            <span className="text-font-primary text-base">
                                Order Details
                            </span>
                        </span>
                    </div>
                )}

                {/* Orders List */}
                {orders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full rounded-lg p-8">
                        <Icon icon="material-symbols:shopping-cart-off-outline" className="text-6xl text-bgAccent mb-4" />
                        <h3 className="text-xl text-font-primary mb-2">No Orders Found</h3>
                        <p className="text-font-secondary text-center">
                            No orders have been placed during calls yet. Orders will appear here when customers make bookings through phone conversations.
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-1 gap-2 h-full min-h-0">
                        {/* Orders Table */}
                        <div className={`flex flex-col w-full transition-all duration-300 ease-out
                            ${selectedOrder ? 'hidden 2xl:flex 2xl:w-1/2' : 'w-full'}
                        `}>
                            <div ref={tableBodyRef} className="w-full h-full flex flex-col overflow-hidden">
                                {/* Desktop Table */}
                                <OrdersTable 
                                    paginatedOrders={orders}
                                    selectedOrder={selectedOrder}
                                    searchParams={searchParams}
                                    setSelectedOrder={setSelectedOrder}
                                    rowRef={rowRef}
                                    getEquipmentList={getEquipmentList}
                                    getOrderStatus={getOrderStatus}
                                    getOrderStatusColor={getOrderStatusColor}
                                    getTotalAmount={getTotalAmount}
                                />

                                {/* Mobile Header (below xl) */}
                                <div className="flex-shrink-0 md:hidden">
                                    <div className="flex items-center justify-between px-3 py-2 text-font-primary font-semibold text-sm h-[48px] card-container">
                                        <span className="">Orders</span>
                                        <span className="text-xs">
                                            {orders.length} displayed
                                        </span>
                                    </div>
                                </div>

                                {/* Mobile Card View Container */}
                                <div className="flex-1 overflow-y-auto md:hidden">
                                    <OrdersCards 
                                        paginatedOrders={orders}
                                        selectedOrder={selectedOrder}
                                        searchParams={searchParams}
                                        setSelectedOrder={setSelectedOrder}
                                        cardRef={cardRef}
                                        getEquipmentList={getEquipmentList}
                                        getOrderStatus={getOrderStatus}
                                        getOrderStatusColor={getOrderStatusColor}
                                        getTotalAmount={getTotalAmount}
                                    />

                                    {orders.length === 0 && (
                                        <div className="text-center py-10 text-gray-400">No orders found.</div>
                                    )}
                                </div>

                            </div>
                        </div>

                        {/* Order Details Panel */}
                        <OrderDetails 
                            selectedOrder={selectedOrder}
                            searchParams={searchParams}
                            setSelectedOrder={setSelectedOrder}
                            getOrderStatus={getOrderStatus}
                            getOrderStatusColor={getOrderStatusColor}
                            handleMarkCompleted={handleMarkCompleted}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default Orders;

